/**
 * The copyright year, in one place, resolved at render time.
 *
 * WHY THIS EXISTS
 * The notice was written out in full in 26 places — the footer, both keys in
 * ten language files, and five server email templates — each with `2025` typed
 * into the sentence. Every one of them was correct on the day it was written
 * and silently wrong from 1 January onwards, on a line whose entire job is to
 * state the current year. Nothing failed, nothing logged, and a stale year on a
 * copyright notice is exactly the kind of thing everyone sees and nobody
 * reports.
 *
 * So the year is never stored. Strings carry a `{year}` placeholder and the
 * value is substituted when the string is rendered.
 *
 * THE REGEX FALLBACK IS LOAD-BEARING, NOT BELT-AND-BRACES
 * Translations do not only come from the files in this repo: `useTranslation`
 * prefers rows from `translations_registry`, which were generated from the old
 * strings and therefore have `2025` baked into them for every non-English
 * language. Those rows cannot be fixed by editing this repo, and `/translationsync`
 * is additive — it will not rewrite a row whose key still exists. Substituting
 * only `{year}` would leave every translated footer pinned to 2025 while the
 * English one advanced.
 *
 * So a literal year is rewritten too. That is safe here because the only
 * strings passed through this are copyright notices, where a four-digit year is
 * the year of the notice by definition.
 */

/** Matches the year in a copyright notice. Deliberately narrow: 19xx/20xx on a
 *  word boundary, first occurrence only. */
const YEAR = /\b(?:19|20)\d{2}\b/;

export const COPYRIGHT_PLACEHOLDER = "{year}";

export function currentCopyrightYear(now: Date = new Date()): number {
  return now.getFullYear();
}

/**
 * Resolve a copyright notice to the current year.
 *
 * Handles both shapes: the `{year}` placeholder this repo now uses, and a
 * hardcoded year arriving from a translation row written before it did.
 */
export function withCurrentYear(text: string, now: Date = new Date()): string {
  const year = String(currentCopyrightYear(now));
  if (text.includes(COPYRIGHT_PLACEHOLDER)) return text.split(COPYRIGHT_PLACEHOLDER).join(year);
  return text.replace(YEAR, year);
}

/** The plain English notice, for surfaces with no translation layer — the
 *  transactional emails, which are sent in English. */
export function copyrightNotice(now: Date = new Date()): string {
  return `© ${currentCopyrightYear(now)} SaleFish Inc. All rights reserved.`;
}
